import { useState, useMemo } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Plus, Trash2, ShieldCheck, AlertCircle, Loader2 } from "lucide-react";

/**
 * MasterPolicyApplicationForm
 *
 * Reusable form for Stage-1 Allianz Trade master-policy application.
 * Used in BOTH the seller registration Step 5 AND the /seller/insurance
 * tab "Apply for coverage" CTA.
 *
 * Props:
 *   onSubmit({buyers, requested_aggregate_limit}) → Promise (caller does API)
 *   onCancel?                                     → optional secondary action
 *   submitting?                                   → external loading flag
 *   error?                                        → external error message
 *   initialBuyers?                                → for reapply / edit flows
 *   submitLabel?
 *   compact?                                      → tighter layout for inline use
 */
export const MasterPolicyApplicationForm = ({
  onSubmit,
  onCancel,
  submitting = false,
  error = "",
  initialBuyers,
  submitLabel = "Submit application",
  compact = false,
}) => {
  const emptyRow = () => ({
    company_name: "",
    ein: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "US",
    annual_sales_volume_last_12mo: "",
    annual_sales_volume_next_12mo: "",
  });

  const [buyers, setBuyers] = useState(
    initialBuyers && initialBuyers.length >= 5
      ? initialBuyers.map((b) => ({ ...emptyRow(), ...b }))
      : Array.from({ length: 5 }, emptyRow),
  );
  const [manualAggregate, setManualAggregate] = useState("");
  const [touched, setTouched] = useState(false);

  const autoAggregate = useMemo(() => {
    const sum = buyers.reduce(
      (acc, b) => acc + (Number(b.annual_sales_volume_next_12mo) || 0),
      0,
    );
    return Math.round(sum * 0.3);
  }, [buyers]);

  const effectiveAggregate = Number(manualAggregate) || autoAggregate;

  const updateRow = (idx, key, val) => {
    setBuyers((rs) => rs.map((r, i) => (i === idx ? { ...r, [key]: val } : r)));
  };

  const addRow = () => setBuyers((rs) => [...rs, emptyRow()]);
  const removeRow = (idx) => {
    if (buyers.length <= 5) return;
    setBuyers((rs) => rs.filter((_, i) => i !== idx));
  };

  // Validation: 5+ buyers, every required field filled, EIN looks valid.
  const einRe = /^\d{2}-?\d{7}$/;
  const validateRow = (b) =>
    b.company_name.trim().length >= 2 &&
    einRe.test(b.ein.trim()) &&
    b.address_line1.trim().length >= 2 &&
    b.city.trim().length >= 1 &&
    b.state.trim().length >= 1 &&
    b.postal_code.trim().length >= 3 &&
    Number(b.annual_sales_volume_last_12mo) >= 0 &&
    String(b.annual_sales_volume_last_12mo).trim() !== "" &&
    Number(b.annual_sales_volume_next_12mo) >= 0 &&
    String(b.annual_sales_volume_next_12mo).trim() !== "";

  const allValid = buyers.length >= 5 && buyers.every(validateRow);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setTouched(true);
    if (!allValid || submitting) return;
    const payload = {
      buyers: buyers.map((b) => ({
        company_name: b.company_name.trim(),
        ein: b.ein.trim(),
        address_line1: b.address_line1.trim(),
        address_line2: b.address_line2.trim() || undefined,
        city: b.city.trim(),
        state: b.state.trim(),
        postal_code: b.postal_code.trim(),
        country: b.country || "US",
        annual_sales_volume_last_12mo: Number(b.annual_sales_volume_last_12mo),
        annual_sales_volume_next_12mo: Number(b.annual_sales_volume_next_12mo),
      })),
      requested_aggregate_limit: effectiveAggregate,
    };
    await onSubmit(payload);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={compact ? "space-y-4" : "space-y-6"}
      data-testid="mp-app-form"
    >
      <div className="rounded-xl border border-foreground/10 bg-emerald-50/40 px-4 py-3 flex items-start gap-3">
        <ShieldCheck className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
        <div className="text-[12.5px] text-foreground/80 leading-snug">
          <strong>Allianz Trade master-policy application</strong> — list at
          least 5 buyers with EIN, legal address, and annual sales volume.
          Underwriting typically takes 3–10 business days. Once approved,
          per-buyer covers are issued automatically as new buyers sign up.
        </div>
      </div>

      {/* Buyer roster */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-[13px]">
            Buyer roster{" "}
            <span className="text-foreground/50">
              ({buyers.length} · min 5 required)
            </span>
          </Label>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={addRow}
            data-testid="mp-add-buyer"
            className="text-[12.5px] gap-1"
          >
            <Plus className="w-3.5 h-3.5" /> Add buyer
          </Button>
        </div>

        {buyers.map((b, idx) => {
          const invalid = touched && !validateRow(b);
          return (
            <div
              key={idx}
              data-testid={`mp-buyer-row-${idx}`}
              className={`rounded-xl border px-4 py-4 space-y-3 ${
                invalid
                  ? "border-rose-300 bg-rose-50/30"
                  : "border-foreground/10 bg-white/60"
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="text-[11px] mono uppercase tracking-wider text-foreground/55">
                  Buyer #{idx + 1}
                </div>
                {buyers.length > 5 && (
                  <button
                    type="button"
                    onClick={() => removeRow(idx)}
                    data-testid={`mp-remove-buyer-${idx}`}
                    className="text-foreground/45 hover:text-rose-700 transition"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11.5px] text-foreground/70">
                    Legal company name
                  </Label>
                  <Input
                    value={b.company_name}
                    onChange={(e) => updateRow(idx, "company_name", e.target.value)}
                    placeholder="Acme Distribution LLC"
                    data-testid={`mp-buyer-${idx}-name`}
                    className="h-9 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-[11.5px] text-foreground/70">EIN</Label>
                  <Input
                    value={b.ein}
                    onChange={(e) => updateRow(idx, "ein", e.target.value)}
                    placeholder="12-3456789"
                    data-testid={`mp-buyer-${idx}-ein`}
                    className="h-9 mt-1 mono"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-[11.5px] text-foreground/70">
                    Address line 1
                  </Label>
                  <Input
                    value={b.address_line1}
                    onChange={(e) => updateRow(idx, "address_line1", e.target.value)}
                    placeholder="500 Madison Ave"
                    data-testid={`mp-buyer-${idx}-addr1`}
                    className="h-9 mt-1"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-[11.5px] text-foreground/70">
                    Address line 2 <span className="text-foreground/45">(optional)</span>
                  </Label>
                  <Input
                    value={b.address_line2}
                    onChange={(e) => updateRow(idx, "address_line2", e.target.value)}
                    placeholder="Suite 4500"
                    data-testid={`mp-buyer-${idx}-addr2`}
                    className="h-9 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-[11.5px] text-foreground/70">City</Label>
                  <Input
                    value={b.city}
                    onChange={(e) => updateRow(idx, "city", e.target.value)}
                    placeholder="New York"
                    data-testid={`mp-buyer-${idx}-city`}
                    className="h-9 mt-1"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[11.5px] text-foreground/70">State</Label>
                    <Input
                      value={b.state}
                      onChange={(e) => updateRow(idx, "state", e.target.value.toUpperCase())}
                      placeholder="NY"
                      maxLength={3}
                      data-testid={`mp-buyer-${idx}-state`}
                      className="h-9 mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-[11.5px] text-foreground/70">ZIP</Label>
                    <Input
                      value={b.postal_code}
                      onChange={(e) => updateRow(idx, "postal_code", e.target.value)}
                      placeholder="10022"
                      data-testid={`mp-buyer-${idx}-zip`}
                      className="h-9 mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-[11.5px] text-foreground/70">
                    Sales last 12 mo ($)
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    value={b.annual_sales_volume_last_12mo}
                    onChange={(e) => updateRow(idx, "annual_sales_volume_last_12mo", e.target.value)}
                    placeholder="250000"
                    data-testid={`mp-buyer-${idx}-sales-last`}
                    className="h-9 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-[11.5px] text-foreground/70">
                    Expected next 12 mo ($)
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    value={b.annual_sales_volume_next_12mo}
                    onChange={(e) => updateRow(idx, "annual_sales_volume_next_12mo", e.target.value)}
                    placeholder="320000"
                    data-testid={`mp-buyer-${idx}-sales-next`}
                    className="h-9 mt-1"
                  />
                </div>
              </div>

              {invalid && (
                <div className="text-[11.5px] text-rose-700 flex items-center gap-1.5">
                  <AlertCircle className="w-3.5 h-3.5" />
                  Fill all required fields. EIN must be 9 digits (e.g. 12-3456789).
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Aggregate limit */}
      <div className="rounded-xl border border-foreground/10 bg-white/60 px-4 py-4 space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-[13px]">Requested aggregate limit</Label>
          <span className="text-[11px] mono text-foreground/55">
            auto-suggest: ${autoAggregate.toLocaleString()}
          </span>
        </div>
        <div className="grid sm:grid-cols-2 gap-3 items-end">
          <div>
            <Label className="text-[11.5px] text-foreground/70">
              Override (USD) <span className="text-foreground/45">— leave blank for auto</span>
            </Label>
            <Input
              type="number"
              min="0"
              value={manualAggregate}
              onChange={(e) => setManualAggregate(e.target.value)}
              placeholder={String(autoAggregate)}
              data-testid="mp-aggregate-override"
              className="h-9 mt-1"
            />
          </div>
          <div className="text-[12.5px] text-foreground/65 leading-snug">
            Auto-suggest = 30% of sum of expected next-12-mo sales across all
            buyers (typical rolling AR exposure).
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50/60 px-3.5 py-2.5 text-[12.5px] text-rose-700" data-testid="mp-error">
          {error}
        </div>
      )}

      <div className="flex items-center gap-3 pt-1">
        {onCancel && (
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
            disabled={submitting}
            data-testid="mp-cancel"
            className="rounded-full h-11"
          >
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          disabled={!allValid || submitting}
          data-testid="mp-submit"
          className="flex-1 h-11 rounded-full bg-foreground text-background hover:bg-foreground/90 text-[14px] font-medium disabled:opacity-50"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…
            </>
          ) : (
            submitLabel
          )}
        </Button>
      </div>
    </form>
  );
};

export default MasterPolicyApplicationForm;
